function getTooltipText(value, instance) {
  if (typeof value === 'string') return value
  if (!value || typeof value !== 'object') return ''
  if (typeof value.content === 'string') return value.content

  if (value.ref && instance?.$refs?.[value.ref]) {
    const ref = instance.$refs[value.ref]
    if (typeof ref === 'string') return ref
    if (ref?.textContent) return ref.textContent.trim()
    if (ref?.$el?.textContent) return ref.$el.textContent.trim()
  }

  if (typeof value.html === 'string') {
    const element = document.getElementById(value.html)
    if (element?.textContent) return element.textContent.trim()
  }

  if (value.html?.textContent) return value.html.textContent.trim()

  return ''
}

function resolveTooltipSource(value, instance) {
  if (!value || typeof value !== 'object') return null

  if (value.ref && instance?.$refs?.[value.ref]) {
    const ref = instance.$refs[value.ref]
    return ref?.$el || ref
  }

  if (typeof value.html === 'string') {
    return document.getElementById(value.html)
  }

  return value.html instanceof HTMLElement ? value.html : null
}

function hideTooltipSource(value, instance) {
  const source = resolveTooltipSource(value, instance)
  if (!(source instanceof HTMLElement)) return

  source.style.display = 'none'
  source.setAttribute('aria-hidden', 'true')
}

function getTooltipPlacement(binding) {
  if (binding.modifiers?.left) return 'left'
  if (binding.modifiers?.right) return 'right'
  if (binding.modifiers?.bottom) return 'bottom'
  return 'top'
}

function removeTooltip(el) {
  const state = el.__legacyTooltip

  if (!state) return

  el.removeEventListener('mouseenter', state.show)
  el.removeEventListener('mouseleave', state.hide)
  el.removeEventListener('focus', state.show)
  el.removeEventListener('blur', state.hide)
  state.tooltip?.remove()
  delete el.__legacyTooltip
}

function positionTooltip(el, tooltip, placement) {
  const targetRect = el.getBoundingClientRect()
  const tooltipRect = tooltip.getBoundingClientRect()
  const gap = 8
  let top = targetRect.top - tooltipRect.height - gap
  let left = targetRect.left + (targetRect.width - tooltipRect.width) / 2

  if (placement === 'bottom') {
    top = targetRect.bottom + gap
  }

  if (placement === 'left') {
    top = targetRect.top + (targetRect.height - tooltipRect.height) / 2
    left = targetRect.left - tooltipRect.width - gap
  }

  if (placement === 'right') {
    top = targetRect.top + (targetRect.height - tooltipRect.height) / 2
    left = targetRect.right + gap
  }

  tooltip.style.top = `${Math.max(4, top + window.scrollY)}px`
  tooltip.style.left = `${Math.max(4, left + window.scrollX)}px`
}

function showTooltip(el) {
  const state = el.__legacyTooltip

  if (!state?.text || state.visible === false) return

  state.tooltip?.remove()

  const tooltip = document.createElement('div')
  tooltip.className = ['vue-tooltip', state.className].filter(Boolean).join(' ')
  tooltip.textContent = state.text
  tooltip.style.position = 'absolute'
  tooltip.style.pointerEvents = 'none'
  tooltip.style.padding = '5px 9px'
  tooltip.style.borderRadius = '4px'
  tooltip.style.fontSize = '14px'
  tooltip.style.lineHeight = '21px'
  tooltip.style.maxWidth = '320px'
  tooltip.style.whiteSpace = 'normal'
  tooltip.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)'
  document.body.appendChild(tooltip)

  state.tooltip = tooltip
  positionTooltip(el, tooltip, state.placement)
}

function applyTooltip(el, binding) {
  if (!(el instanceof HTMLElement)) return

  const text = getTooltipText(binding.value, binding.instance)
  const visible = binding.value?.visible
  const className = typeof binding.value === 'object' ? binding.value.class : ''
  const placement = getTooltipPlacement(binding)
  hideTooltipSource(binding.value, binding.instance)
  removeTooltip(el)

  if (!text || visible === false) {
    el.removeAttribute('title')
    el.removeAttribute('aria-label')
    return
  }

  el.setAttribute('title', text)
  el.setAttribute('aria-label', text)
  el.__legacyTooltip = {
    className,
    hide: () => el.__legacyTooltip?.tooltip?.remove(),
    placement,
    show: () => showTooltip(el),
    text,
    visible
  }
  el.addEventListener('mouseenter', el.__legacyTooltip.show)
  el.addEventListener('mouseleave', el.__legacyTooltip.hide)
  el.addEventListener('focus', el.__legacyTooltip.show)
  el.addEventListener('blur', el.__legacyTooltip.hide)
}

export function installTooltip(target) {
  target.directive('tooltip', {
    mounted(el, binding) {
      applyTooltip(el, binding)
    },
    updated(el, binding) {
      applyTooltip(el, binding)
    },
    beforeUnmount(el) {
      if (!(el instanceof HTMLElement)) return
      removeTooltip(el)
      el.removeAttribute('title')
      el.removeAttribute('aria-label')
    }
  })
}
